if (!customElements.get('trapt-cart-quick-add')) {
  customElements.define(
    'trapt-cart-quick-add',
    class TraptCartQuickAdd extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;

        this.dataset.ready = 'true';
        this.trigger = this.querySelector('[data-t74-open]');
        this.dialog = this.querySelector('.t74-cart-quick-add__dialog');
        this.closeButton = this.querySelector('[data-t74-close]');
        /* One dropdown per option; the variant is whatever the pair resolves to. */
        this.optionSelects = Array.from(this.querySelectorAll('[data-t74-option]'));
        this.variantIdInput = this.querySelector('[data-t74-variant-id]');
        this.unavailable = this.querySelector('[data-t74-unavailable]');
        this.variants = this.parseVariants();
        this.submitButton = this.querySelector('.t74-cart-quick-add__submit');
        this.price = this.querySelector('[data-t74-price]');

        if (!this.trigger || !this.dialog) return;

        this.trigger.addEventListener('click', () => this.open());
        this.closeButton?.addEventListener('click', () => this.close());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        this.dialog.addEventListener('close', () => {
          this.trigger.setAttribute('aria-expanded', 'false');
          const drawer = this.closest('cart-drawer');
          if (drawer?.classList.contains('active')) trapFocus(drawer.querySelector('#CartDrawer'), this.trigger);
          this.trigger.focus({ preventScroll: true });
        });
        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') event.stopPropagation();
        });
        this.dialog.addEventListener('keyup', (event) => {
          if (event.key === 'Escape') event.stopPropagation();
        });
        this.trigger.addEventListener('keyup', (event) => {
          if (event.key === 'Escape') event.stopPropagation();
        });

        this.optionSelects.forEach((select) => {
          select.addEventListener('change', (event) => {
            event.stopPropagation();
            this.syncVariant();
          });
        });

        this.syncVariant();
      }

      parseVariants() {
        const node = this.querySelector('[data-t74-variants]');
        if (!node) return [];
        try {
          const parsed = JSON.parse(node.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      /* An option is a dropdown or a row of image swatches; read whichever. */
      chosenValues() {
        const byIndex = [];
        this.optionSelects.forEach((control) => {
          const index = Number.parseInt(control.dataset.t74Option, 10);
          if (Number.isNaN(index)) return;
          if (control.tagName === 'SELECT') {
            byIndex[index] = control.value;
            return;
          }
          if (control.checked) byIndex[index] = control.value;
        });
        return byIndex;
      }

      /* Is there a sellable variant if this one option takes this value? */
      canTake(index, value, chosen) {
        const combo = chosen.slice();
        combo[index] = value;
        return this.variants.some(
          (candidate) =>
            candidate.available &&
            Array.isArray(candidate.options) &&
            candidate.options.every((current, position) => current === combo[position])
        );
      }

      /* Cross out the values that cannot be had with the rest of the choice. */
      markAvailability(chosen) {
        this.optionSelects.forEach((control) => {
          const index = Number.parseInt(control.dataset.t74Option, 10);
          if (Number.isNaN(index)) return;

          if (control.tagName === 'SELECT') {
            /* Grey the impossible rows out in the list itself rather than
               appending words the closed pill has no room to show. */
            Array.from(control.options).forEach((option) => {
              option.disabled = option.value !== control.value && !this.canTake(index, option.value, chosen);
            });
            return;
          }

          const label = this.querySelector(`label[for="${control.id}"]`);
          label?.classList.toggle('is-unavailable', !this.canTake(index, control.value, chosen));
        });
      }

      /* "Anfield Edition is available in Large." beats a dead end. */
      unavailableMessage(chosen) {
        const changedIndex = this.optionSelects.findIndex(
          (control) => control.tagName !== 'SELECT' && control.checked && !this.canTake(Number(control.dataset.t74Option), control.value, chosen)
        );
        const control = changedIndex > -1 ? this.optionSelects[changedIndex] : null;
        if (!control) return 'That combination is sold out.';
        const index = Number.parseInt(control.dataset.t74Option, 10);
        const others = this.variants
          .filter((candidate) => candidate.available && candidate.options[index] === control.value)
          .map((candidate) => candidate.options.filter((value, position) => position !== index).join(' · '));
        const unique = [...new Set(others)];
        if (!unique.length) return `${control.value} is sold out.`;
        return `${control.value} is available in ${unique.join(', ')}.`;
      }

      syncVariant() {
        if (!this.optionSelects.length) return;
        const chosen = this.chosenValues();
        const variant = this.variants.find(
          (candidate) => Array.isArray(candidate.options) && candidate.options.every((value, index) => value === chosen[index])
        );
        const sellable = Boolean(variant && variant.available);

        this.markAvailability(chosen);

        if (this.variantIdInput) this.variantIdInput.value = sellable ? variant.id : '';
        if (this.unavailable) {
          this.unavailable.hidden = sellable;
          if (!sellable) this.unavailable.textContent = this.unavailableMessage(chosen);
        }
        if (this.submitButton) {
          this.submitButton.disabled = !sellable;
          this.submitButton.toggleAttribute('aria-disabled', !sellable);
        }
        if (this.price && variant && typeof variant.price === 'number') {
          this.price.textContent = this.formatMoney(variant.price);
        }

        const productForm = this.querySelector('product-form');
        if (productForm && sellable) {
          productForm.error = false;
          productForm.handleErrorMessage?.();
          productForm.submitButtonText?.classList.remove('hidden');
          productForm.querySelector('.sold-out-message')?.classList.add('hidden');
        }
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || (window.Shopify && window.Shopify.money_format) || '${{amount}}';
        const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!placeholder) return format;
        const withDelimiters = (precision, thousands, decimal) => {
          const parts = (Number(cents) / 100).toFixed(precision).split('.');
          const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return parts[1] ? whole + decimal + parts[1] : whole;
        };
        switch (placeholder[1]) {
          case 'amount_no_decimals':
            return format.replace(placeholder[0], withDelimiters(0, ',', '.'));
          case 'amount_with_comma_separator':
            return format.replace(placeholder[0], withDelimiters(2, '.', ','));
          case 'amount_no_decimals_with_comma_separator':
            return format.replace(placeholder[0], withDelimiters(0, '.', ','));
          default:
            return format.replace(placeholder[0], withDelimiters(2, ',', '.'));
        }
      }

      open() {
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
        this.trigger.setAttribute('aria-expanded', 'true');
        this.dialog.showModal();
      }

      close() {
        if (this.dialog.open) this.dialog.close();
      }
    }
  );
}
